/**
 * useTtsPlayback (F3) — wire `tts.chunk` events into a {@link TtsPlayer} and
 * surface `ops.alert` (contingency) events as UI state.
 *
 * The hook owns a single TtsPlayer instance for the lifetime of the component
 * and subscribes to events through a `subscribe` function that mirrors
 * `WsClient.onEvent` / `ViewerConnection.onEvent` (takes a listener, returns an
 * unsubscribe). Passing `null` (e.g. before a session/connection exists) simply
 * arms nothing. The player is disposed on unmount.
 *
 * `ops.alert` events are exposed as `alert`; the contingency code
 * `tts_contingency` (TTS unavailable → subtitle-only mode) is called out via
 * `isContingency` so the UI can show a banner and gate audio controls.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { TtsPlayer, supportsSinkSelection } from '@/features/tts/ttsPlayer';
import type { EventEnvelopeV1, OpsAlertPayload } from '@/shared/models/events';

/** ops.alert code emitted when TTS falls back to subtitle-only (PRD §8). */
export const TTS_CONTINGENCY_CODE = 'tts_contingency' as const;

/** Subscribe to every inbound event; returns an unsubscribe function. */
export type EventSubscribe = (
  listener: (event: EventEnvelopeV1) => void,
) => () => void;

export interface UseTtsPlaybackOptions {
  /** Event source (WsClient / ViewerConnection). `null` arms nothing. */
  subscribe: EventSubscribe | null;
  /** Start muted (e.g. a viewer who wants subtitles only). Default false. */
  initialMuted?: boolean;
  onError?: ((message: string) => void) | undefined;
}

export interface UseTtsPlaybackResult {
  /** Latest ops.alert, or null if none seen. */
  alert: OpsAlertPayload | null;
  /** True once a `tts_contingency` alert has arrived (subtitle-only mode). */
  isContingency: boolean;
  muted: boolean;
  toggleMuted: () => void;
  setMuted: (muted: boolean) => void;
  /** Select the output device (Chromium only; no-op elsewhere). */
  setSinkId: (deviceId: string) => void;
  /** Whether this browser supports output-device selection. */
  sinkSelectionSupported: boolean;
  /** Unlock audio playback from within a user gesture (autoplay policy). */
  unlockAudio: () => void;
}

export function useTtsPlayback(
  options: UseTtsPlaybackOptions,
): UseTtsPlaybackResult {
  const { subscribe, initialMuted = false, onError } = options;

  const playerRef = useRef<TtsPlayer | null>(null);
  const [muted, setMutedState] = useState(initialMuted);
  const [alert, setAlert] = useState<OpsAlertPayload | null>(null);
  const [isContingency, setIsContingency] = useState(false);

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // One player for the component's lifetime.
  if (playerRef.current === null) {
    playerRef.current = new TtsPlayer({
      onError: (message) => onErrorRef.current?.(message),
    });
    playerRef.current.setMuted(initialMuted);
  }

  useEffect(() => {
    return () => {
      playerRef.current?.dispose();
      playerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!subscribe) return;
    const unsubscribe = subscribe((event) => {
      if (event.event_type === 'tts.chunk') {
        const { audio_b64, mime, seq } = event.payload;
        playerRef.current?.enqueue(audio_b64, mime, seq);
        return;
      }
      if (event.event_type === 'ops.alert') {
        const payload = event.payload;
        setAlert(payload);
        if (payload.code === TTS_CONTINGENCY_CODE) {
          setIsContingency(true);
          // No audio will arrive in contingency — drain anything pending.
          playerRef.current?.stop();
        }
      }
    });
    return unsubscribe;
  }, [subscribe]);

  const setMuted = useCallback((next: boolean) => {
    // Unmuting is a user gesture — unlock the audio element so the browser
    // permits playback of subsequent TTS chunks (autoplay policy).
    if (!next) void playerRef.current?.unlock();
    playerRef.current?.setMuted(next);
    setMutedState(next);
  }, []);

  const toggleMuted = useCallback(() => {
    setMutedState((prev) => {
      const next = !prev;
      if (!next) void playerRef.current?.unlock();
      playerRef.current?.setMuted(next);
      return next;
    });
  }, []);

  const setSinkId = useCallback((deviceId: string) => {
    void playerRef.current?.setSinkId(deviceId);
  }, []);

  const unlockAudio = useCallback(() => {
    void playerRef.current?.unlock();
  }, []);

  return {
    alert,
    isContingency,
    muted,
    toggleMuted,
    setMuted,
    setSinkId,
    sinkSelectionSupported: supportsSinkSelection(),
    unlockAudio,
  };
}
